import React, { useState } from "react";
import { Building2, Save } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/** Full customer "Details" view/edit modal — opened via the row-level "View" button on
 *  the Customers page. Separate, dedicated entry points for Contacts (CustomerContactsModal)
 *  and Signed Forms (SignedFormsModal) remain unchanged alongside this. */
export function CustomerDetailModal({ customer, canEdit, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: customer.customer_name || "",
    contact_person: customer.contact_person || "",
    email: customer.email || "",
    phone: customer.phone || "",
    alt_phone: customer.alt_phone || "",
    vat_number: customer.vat_number || "",
    address_line_1: customer.address_line_1 || "",
    address_line_2: customer.address_line_2 || "",
    city: customer.city || "",
    province: customer.province || "",
    postal_code: customer.postal_code || "",
    country: customer.country || "",
    pte_online_enabled: !!customer.pte_online_enabled,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}`, form);
      toast.success("Customer details saved");
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} wide icon={Building2} title={customer.customer_name}
      subtitle={`Customer ${customer.customer_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} data-testid="cust-detail-cancel">Close</button>
        {canEdit && (
          <button className="btn gold" onClick={save} disabled={busy} data-testid="cust-detail-save">
            <Save size={14} /> {busy ? "Saving…" : "Save changes"}
          </button>
        )}
      </>}>
      <div className="form-grid">
        <div className="fld"><label>Company name <span className="req">*</span></label>
          <input className="input" value={form.customer_name} disabled={!canEdit}
            onChange={(e) => set("customer_name", e.target.value)} data-testid="cust-detail-name" /></div>
        <div className="fld"><label>Primary contact</label>
          <input className="input" value={form.contact_person} disabled={!canEdit}
            onChange={(e) => set("contact_person", e.target.value)} data-testid="cust-detail-contact" /></div>
        <div className="fld"><label>Email</label>
          <input className="input" value={form.email} disabled={!canEdit}
            onChange={(e) => set("email", e.target.value)} data-testid="cust-detail-email" /></div>
        <div className="fld"><label>Phone</label>
          <input className="input" value={form.phone} disabled={!canEdit}
            onChange={(e) => set("phone", e.target.value)} data-testid="cust-detail-phone" /></div>
        <div className="fld"><label>Alt. phone</label>
          <input className="input" value={form.alt_phone} disabled={!canEdit}
            onChange={(e) => set("alt_phone", e.target.value)} data-testid="cust-detail-altphone" /></div>
        <div className="fld"><label>VAT number</label>
          <input className="input" value={form.vat_number} disabled={!canEdit}
            onChange={(e) => set("vat_number", e.target.value)} data-testid="cust-detail-vat" /></div>
        <div className="fld"><label>Address line 1</label>
          <input className="input" value={form.address_line_1} disabled={!canEdit}
            onChange={(e) => set("address_line_1", e.target.value)} data-testid="cust-detail-addr1" /></div>
        <div className="fld"><label>Address line 2</label>
          <input className="input" value={form.address_line_2} disabled={!canEdit}
            onChange={(e) => set("address_line_2", e.target.value)} data-testid="cust-detail-addr2" /></div>
        <div className="fld"><label>City</label>
          <input className="input" value={form.city} disabled={!canEdit}
            onChange={(e) => set("city", e.target.value)} data-testid="cust-detail-city" /></div>
        <div className="fld"><label>Province</label>
          <input className="input" value={form.province} disabled={!canEdit}
            onChange={(e) => set("province", e.target.value)} data-testid="cust-detail-province" /></div>
        <div className="fld"><label>Postal code</label>
          <input className="input" value={form.postal_code} disabled={!canEdit}
            onChange={(e) => set("postal_code", e.target.value)} data-testid="cust-detail-postal" /></div>
        <div className="fld"><label>Country</label>
          <input className="input" value={form.country} disabled={!canEdit}
            onChange={(e) => set("country", e.target.value)} data-testid="cust-detail-country" /></div>
      </div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, fontSize: 13 }}>
        <input type="checkbox" checked={form.pte_online_enabled} disabled={!canEdit}
          onChange={(e) => set("pte_online_enabled", e.target.checked)} data-testid="cust-detail-portal" />
        Enable customer portal access
      </label>
    </Modal>
  );
}
